import { AlertTriangle, RefreshCw, Radio, Square } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadProduction,
  refreshObsAudioSource,
  stopObsStream,
  switchObsScene,
  type ProductionSnapshot,
} from './stream-api';
import './workspace.css';

export function ObsPage() {
  const [production, setProduction] = useState<ProductionSnapshot | null>(null);
  const [selectedScene, setSelectedScene] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const next = await loadProduction();
      setProduction(next);
      setSelectedScene((current) => current && next.obs.scenes.includes(current) ? current : next.obs.currentProgramSceneName ?? next.obs.scenes[0] ?? '');
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'OBS state could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 3000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const obsReady = production?.activeAgent?.connected === true && production.obs.status === 'online';
  const currentScene = production?.obs.currentProgramSceneName ?? null;
  const sceneGroups = useMemo(() => {
    const scenes = production?.obs.scenes ?? [];
    return {
      likelyLive: scenes.filter((scene) => !/start|intro|brb|away|end|ending/i.test(scene)),
      utility: scenes.filter((scene) => /start|intro|brb|away|end|ending/i.test(scene)),
    };
  }, [production]);

  async function run(operation: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await operation();
      setMessage(success);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'OBS action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function changeScene() {
    if (!selectedScene || selectedScene === currentScene) return;
    await run(() => switchObsScene(selectedScene), `Switched OBS to “${selectedScene}”.`);
  }

  async function emergencyStop() {
    if (!production?.obs.streaming) return;
    if (!window.confirm('END THE LIVE STREAM NOW?\n\nThis tells OBS to stop streaming immediately. It does not change scenes or close OBS.')) return;
    await run(stopObsStream, 'OBS live stream stopped.');
  }

  if (loading && !production) return <div className="ffs-next-loading">Loading OBS…</div>;

  return (
    <div className="ffs-workspace-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      {production ? (
        <>
          <section className="ffs-workspace-hero">
            <div>
              <small>OBS CONTROL</small>
              <h2>{obsReady ? currentScene || 'OBS connected' : 'OBS unavailable'}</h2>
              <p>{obsReady ? `${production.obs.streaming ? 'Streaming' : 'Not streaming'} · ${production.obs.recording ? 'Recording' : 'Not recording'}` : production.obs.lastError || 'The Stream PC or OBS connection needs checking.'}</p>
            </div>
            <button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={busy}><RefreshCw size={15} /> Refresh</button>
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>LIVE STATE</small><h3>OBS connection</h3></div><strong className={obsReady ? 'ffs-status good' : 'ffs-status bad'}>{obsReady ? 'ONLINE' : 'OFFLINE'}</strong></header>
            <div className="ffs-fact-grid">
              <div><span>Current scene</span><strong>{currentScene || '—'}</strong></div>
              <div><span>Streaming</span><strong>{production.obs.streaming ? 'Yes' : 'No'}</strong></div>
              <div><span>Recording</span><strong>{production.obs.recording ? 'Yes' : 'No'}</strong></div>
              <div><span>OBS version</span><strong>{production.obs.obsVersion || 'Unknown'}</strong></div>
              <div><span>WebSocket</span><strong>{production.obs.obsWebSocketVersion || 'Unknown'}</strong></div>
              <div><span>Stream PC Agent</span><strong>{production.activeAgent?.connected ? production.activeAgent.agentVersion || 'Connected' : 'Offline'}</strong></div>
            </div>
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>ROUTINE CONTROL</small><h3>Change scene</h3></div><Radio size={18} /></header>
            {production.obs.scenes.length === 0 ? (
              <div className="ffs-empty-state"><AlertTriangle size={18} /> No OBS scenes have been reported.</div>
            ) : (
              <>
                <div className="ffs-scene-control">
                  <label><span>Scene</span><select value={selectedScene} disabled={busy || !obsReady} onChange={(event) => setSelectedScene(event.target.value)}>{production.obs.scenes.map((scene) => <option key={scene} value={scene}>{scene}</option>)}</select></label>
                  <button type="button" className="ffs-button primary" disabled={busy || !obsReady || !selectedScene || selectedScene === currentScene} onClick={() => void changeScene()}>Switch Scene</button>
                </div>
                <div className="ffs-scene-shortcuts">
                  {sceneGroups.likelyLive.slice(0, 8).map((scene) => (
                    <button type="button" key={scene} className={scene === currentScene ? 'current' : ''} disabled={busy || !obsReady || scene === currentScene} onClick={() => { setSelectedScene(scene); void run(() => switchObsScene(scene), `Switched OBS to “${scene}”.`); }}>{scene}</button>
                  ))}
                </div>
                {sceneGroups.utility.length ? <details className="ffs-disclosure"><summary>Utility / intro / ending scenes</summary><div className="ffs-scene-shortcuts">{sceneGroups.utility.map((scene) => <button type="button" key={scene} className={scene === currentScene ? 'current' : ''} disabled={busy || !obsReady || scene === currentScene} onClick={() => { setSelectedScene(scene); void run(() => switchObsScene(scene), `Switched OBS to “${scene}”.`); }}>{scene}</button>)}</div></details> : null}
              </>
            )}
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>AUDIO SOURCE</small><h3>FFSControl browser audio</h3></div></header>
            <div className="ffs-primary-action">
              <div><strong>{production.soundboard.overlayClients > 0 ? 'Audio source connected' : 'Audio source needs checking'}</strong><span>{production.soundboard.overlayClients} browser client{production.soundboard.overlayClients === 1 ? '' : 's'} · {production.soundboard.obsProvisioned ? 'OBS source provisioned' : 'OBS source not confirmed'}</span></div>
              <button type="button" className="ffs-button secondary" disabled={busy || !production.activeAgent?.connected} onClick={() => void run(refreshObsAudioSource, 'FFSControl audio source refreshed in OBS.')}>Refresh Audio Source</button>
            </div>
          </section>

          <section className="ffs-workspace-card danger-zone">
            <header><div><small>EMERGENCY</small><h3>Stop live stream</h3></div><Square size={17} /></header>
            <p>This is intentionally separate from normal scene controls. It sends OBS an immediate stop-stream command and requires confirmation.</p>
            <button type="button" className="ffs-button danger" disabled={busy || !production.obs.streaming} onClick={() => void emergencyStop()}>End Live Stream Now</button>
          </section>
        </>
      ) : null}
    </div>
  );
}
